/**
 * Rastreio — **visão unificada** do estado interno da expedição + estado do
 * transportador (Task 14, brief 3.7 / D-14.4).
 *
 * Esta tela existe para responder "onde está cada entrega", juntando o que a
 * Super2 executa (etapa interna) com o que o transportador reporta (rastreio
 * simulado sem credencial de transportadora). A classificação da relação entre
 * os dois lados (`aligned`/`tracking_ahead`/`internal_ahead`/`tracking_missing`/
 * `tracking_exception`) vem da regra pura — a tela não decide, só mostra e
 * sinaliza a divergência em vez de esconder um dos lados.
 *
 * Migrado para `.tsx`/JSX real (Cluster D — Logística & Garantias, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * apenas (`createElement` → JSX): a tela já estava tokenizada e já tinha o
 * ícone `TriangleAlert` no selo de divergência (achado da investigação
 * inicial do cluster) — nenhum ícone novo foi adicionado. Nenhum `data-*`/
 * aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `FulfillmentStateMachine.tsx`.
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, MapPin, TriangleAlert } from "lucide-react";
import { EmptyState, PageHeader } from "../ui/index.js";
import { RecordTable } from "../components/RecordTable.js";
import { FulfillmentBoundary } from "../components/FulfillmentBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { isFulfillmentSourceAwaitingContract, unifiedView } from "../fulfillment/dataset.js";
import { FULFILLMENT_STAGE_LABELS, type FulfillmentSyncState } from "../fulfillment/state-machine.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import type { FulfillmentDataset } from "../fulfillment/dataset.js";
import type { RastreioViewProps } from "../fulfillment/view-props.js";
import type { FulfillmentTask } from "../api/types.js";

const TRACKING_STATUS_LABELS: Record<string, string> = {
  confirmed: "Confirmado",
  shipped: "Postado",
  in_transit: "Em trânsito",
  delivered: "Entregue",
  exception: "Exceção"
};

const SYNC_LABELS: Record<FulfillmentSyncState, string> = {
  aligned: "Alinhado",
  tracking_ahead: "Transportador à frente",
  internal_ahead: "Expedição à frente",
  tracking_missing: "Sem evento do transportador",
  tracking_exception: "Exceção do transportador"
};

export function RastreioTable({ tasks }: { tasks: FulfillmentTask[] }): ReactElement {
  return (
    <RecordTable<FulfillmentTask>
      caption="Rastreio unificado: estado interno da expedição e estado do transportador"
      rows={tasks}
      idOf={(task) => task.id}
      emptyMessage="Nenhuma expedição para rastrear neste recorte."
      // Fase 8 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
      animateRows
      columns={[
        {
          key: "order",
          label: "Pedido",
          render: (task) => (
            <span data-order-external-id={task.orderExternalId}>
              <Link to={`/pedidos/${task.platformOrderId}`}>{task.orderExternalId}</Link>
            </span>
          )
        },
        {
          key: "expedicao",
          label: "Expedição",
          render: (task) => <Link to={`/expedicao/${task.id}`}>{task.id}</Link>
        },
        {
          key: "stage",
          label: "Etapa interna",
          render: (task) => <span data-fulfillment-stage={task.stage}>{FULFILLMENT_STAGE_LABELS[task.stage]}</span>
        },
        {
          key: "tracking",
          label: "Transportador",
          render: (task) => (
            <span data-tracking-status={task.tracking.status ?? ""}>
              {task.tracking.status === null ? "sem evento ainda" : TRACKING_STATUS_LABELS[task.tracking.status] ?? task.tracking.status}
            </span>
          )
        },
        {
          key: "sync",
          label: "Sincronização",
          render: (task) => {
            const view = unifiedView(task);
            // Divergência interno↔transportador: ícone+texto (nunca só cor) via
            // field--pending tokenizado; linha alinhada fica neutra.
            if (view.sync === "aligned") {
              return (
                <span data-fulfillment-sync={view.sync} title={view.note}>
                  {SYNC_LABELS[view.sync]}
                </span>
              );
            }
            return (
              <span
                data-fulfillment-sync={view.sync}
                className="field--pending inline-flex items-center gap-1 font-semibold text-brand-text"
                title={view.note}
              >
                <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
                <span>{SYNC_LABELS[view.sync]}</span>
              </span>
            );
          }
        },
        {
          key: "code",
          label: "Código de rastreio",
          render: (task) => task.trackingCode ?? "—"
        }
      ]}
    />
  );
}

export function RastreioView({ tasks, taskId = null }: RastreioViewProps): ReactElement {
  if (isFulfillmentSourceAwaitingContract(tasks)) {
    return (
      <div className="page page--rastreio flex flex-col gap-6" data-page="rastreio">
        <PageHeader title="Rastreio" />
        <StatusPanel
          kind="awaiting_source"
          title={`Rastreio unificado ${AWAITING_CONTRACT_LABEL}`}
          detail={
            "A rota /api/platform/fulfillment não respondeu neste ambiente. A visão unificada aparece quando a fila de " +
            "expedição for publicada — sem o estado interno não há o que unificar com o rastreio."
          }
        />
      </div>
    );
  }
  const rows = taskId ? tasks.tasks.filter((task) => task.id === taskId) : tasks.tasks;
  const divergent = rows.filter((task) => unifiedView(task).sync !== "aligned" && unifiedView(task).sync !== "tracking_missing");
  return (
    <div className="page page--rastreio flex flex-col gap-6" data-page="rastreio">
      <PageHeader
        title="Rastreio"
        description="Onde está cada entrega: a etapa interna da Super2 junto do que o transportador reporta."
        actions={
          taskId ? (
            <Link to="/rastreio" className="text-sm font-bold text-brand-text underline">
              Ver todos os rastreios
            </Link>
          ) : null
        }
      />
      <p className="m-0 text-sm font-bold text-muted" data-rastreio-count={rows.length}>
        {taskId ? `Rastreio da expedição ${taskId}.` : `${rows.length} expedição(ões) com rastreio unificado.`}
      </p>
      <p
        className={
          "m-0 flex items-center gap-2 rounded-panel border px-4 py-3 text-sm " +
          (divergent.length === 0 ? "border-border bg-surface text-muted" : "border-warning bg-warning-soft font-bold text-ink")
        }
        data-rastreio-divergent={divergent.length}
      >
        {divergent.length === 0 ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden focusable={false} />
        ) : (
          <TriangleAlert className="h-4 w-4 shrink-0 text-warning" aria-hidden focusable={false} />
        )}
        {divergent.length === 0
          ? "Estado interno e transportador coincidem (ou o transportador ainda não reportou) em todas as linhas."
          : `${divergent.length} expedição(ões) com divergência entre o estado interno e o transportador — mostradas lado a lado, nunca com um lado escondido.`}
      </p>
      {rows.length === 0 ? (
        <EmptyState icon={MapPin} title="Nenhuma expedição para rastrear neste recorte." description="O rastreio aparece assim que a primeira expedição for criada." />
      ) : (
        <RastreioTable tasks={rows} />
      )}
    </div>
  );
}

export function Rastreio(): ReactElement {
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("task");
  return <FulfillmentBoundary>{(tasks: FulfillmentDataset) => <RastreioView tasks={tasks} taskId={taskId} />}</FulfillmentBoundary>;
}
